import React, { useContext, useEffect, useState } from "react"
import { View, ScrollView, StyleSheet, Text } from "react-native"
import moment from "moment"
import "moment/locale/fr"
import * as amplitude from "@amplitude/analytics-react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import BrevetFunnelHeader from "../brevet/components/BrevetFunnelHeader"
import { useSummerFunnel } from "./context/SummerFunnelContext"
import { useSummerFunnelGuard } from "./hooks/useSummerFunnelGuard"
import { summerFunnelService } from "./services/summerFunnelService"
import { SUMMER_SLOTS, resolveSummerPricing } from "./constants/summerContent"
import { formatEuros } from "../brevet/utils/formatEuros"

/**
 * Écran de confirmation post-paiement : 1 € payé + abo Cours d'été à venir +
 * créneaux + prochaines étapes. L'email de confirmation reste la source de
 * vérité (envoyé par le webhook backend).
 */
const SummerConfirmationScreen = ({ navigation }) => {
    const dimensions = useContext(DimensionsContext)
    const { state } = useSummerFunnel()
    // Refresh-safe / URL-partageable : une ouverture de l'URL de confirmation
    // dans un contexte neuf ré-hydrate via summer/resume (état payé → reste sur
    // la confirmation). Même tab après paiement = pas froid (paid en mémoire).
    const { nextParams } = useSummerFunnelGuard()

    useEffect(() => {
        amplitude.track("summer_funnel_confirmation_viewed", { funnel_state_id: state.funnelStateId })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Prorata figé CÔTÉ SERVEUR à la conversion (mêmes colonnes que l'email de
    // confirmation) — source prioritaire du récap. Le contexte client n'est
    // qu'un fallback d'affichage instantané : il peut être vide quand les
    // start-options n'avaient pas chargé au checkout (échec réseau silencieux),
    // ce qui affichait un plein tarif trompeur au parent.
    const [serverCharge, setServerCharge] = useState(null)
    useEffect(() => {
        if (!state.funnelStateId) return
        let cancelled = false
        summerFunnelService
            .getFunnelState({ stateId: state.funnelStateId })
            .then((res) => {
                if (cancelled) return
                const payload = res?.data?.data ?? res?.data ?? {}
                if (payload.first_charge_amount_cents != null) {
                    setServerCharge({
                        amountCents: payload.first_charge_amount_cents,
                        scheduledAt: payload.first_charge_scheduled_at || null,
                    })
                }
            })
            .catch(() => {
                /* le fallback contexte / copy neutre prend le relais */
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.funnelStateId])

    const firstname = state.studentFirstname || "votre enfant"
    // Créneau = jour + horaire (matin/soir, tous d'1 h) — l'horaire vient du slot.
    const slotLabels = (state.selectedSlotIds || [])
        .map((id) => {
            const slot = SUMMER_SLOTS.find((s) => s.id === id)
            return slot ? `${slot.label} ${slot.time}` : null
        })
        .filter(Boolean)
    const containerWidth = Math.min(dimensions?.width || 400, 460)

    // Prix mensuel réel du lead = fonction du nombre de créneaux (chacun 1 h) ET de
    // la variante d'engagement servie par le lien fast-track (défaut SANS engagement).
    const monthlyPrice = resolveSummerPricing(
        state.selectedSlotIds?.length || 3,
        !!state.withCommitment
    ).price

    // Date réelle du 1ᵉʳ cours, calculée côté backend et propagée dans le
    // funnel (même source que le « Démarrage » du checkout).
    const firstSessionIso = state.firstSession || state.startDate || null
    const firstCourseLabel = firstSessionIso
        ? moment(firstSessionIso).locale("fr").format("dddd Do MMMM")
        : null

    // 1ᵉʳ prélèvement = le jour de la 1ʳᵉ séance, montant proratisé. Priorité au
    // montant FIGÉ SERVEUR (même source que l'email et que le cron qui débite),
    // puis au montant propagé par le checkout ; sans montant fiable, copy neutre
    // renvoyant vers l'email — jamais un plein tarif qui se lirait comme le
    // montant du jour J.
    const chargeAmountCents =
        serverCharge?.amountCents ?? state.firstChargeAmountCents ?? null
    const chargeDateIso = serverCharge?.scheduledAt || firstSessionIso
    const firstChargeLabel = chargeDateIso
        ? moment(chargeDateIso).locale("fr").format("D MMMM")
        : null
    // « au prorata » seulement quand le 1ᵉʳ mois est réellement partiel (un
    // démarrage pré-saison / août-seul facture exactement le mois plein).
    const chargeIsProrated =
        chargeAmountCents != null &&
        chargeAmountCents < Math.round(monthlyPrice * 100)
    const prelevementNote =
        chargeAmountCents != null && firstChargeLabel
            ? `1ᵉʳ prélèvement de ${formatEuros(chargeAmountCents / 100)} le ${firstChargeLabel} (1ʳᵉ séance)${
                  chargeIsProrated ? " — au prorata des séances restantes" : ""
              }`
            : firstChargeLabel
              ? `1ᵉʳ prélèvement le ${firstChargeLabel} (1ʳᵉ séance) — montant détaillé dans l'email de confirmation`
              : "1ᵉʳ prélèvement au démarrage des cours — montant détaillé dans l'email de confirmation"

    return (
        <View style={styles.root}>
            <View style={[styles.frame, { width: containerWidth }]}>
                <BrevetFunnelHeader step={4} total={4} hideBack hideStep hideLogo />
                <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
                    <View style={styles.successIcon}>
                        <FontAwesome5 name="check" size={32} color={theme.colors.success} />
                    </View>
                    <Text style={styles.title}>C'est confirmé 🎉</Text>
                    <Text style={styles.subtitle}>
                        La place de {firstname} aux Cours d'été est réservée.
                    </Text>
                    <Text style={styles.emailHint}>
                        📧 Un email de confirmation vient de partir.
                    </Text>

                    <View style={styles.summary}>
                        <SummaryRow
                            label="Payé aujourd'hui"
                            value={state.skipDeposit ? "0 €" : "1 €"}
                        />
                        <SummaryRow
                            label="Abonnement Cours d'été"
                            value={`${formatEuros(monthlyPrice)}/mois`}
                            note={`${
                                state.withCommitment
                                    ? "Engagement 12 mois"
                                    : "Sans engagement"
                            } · ${prelevementNote}`}
                        />
                        {slotLabels.length > 0 && (
                            <SummaryRow label="Créneaux" value={slotLabels.join(" · ")} isLast />
                        )}
                    </View>

                    <View style={styles.block}>
                        <Text style={styles.blockKicker}>Prochaines étapes</Text>
                        <InfoRow icon="user-friends" text="On attribue un tuteur avant le début des cours." />
                        <InfoRow icon="mobile-alt" text="Téléchargez l'app Masteur pour suivre les séances." />
                        <InfoRow
                            icon="calendar-check"
                            text={
                                firstCourseLabel
                                    ? `1ᵉʳ cours le ${firstCourseLabel} — vous recevez les informations pratiques quelques jours avant.`
                                    : "1ᵉʳ cours bientôt — vous recevez les informations pratiques quelques jours avant."
                            }
                        />
                    </View>
                </ScrollView>
                <View style={styles.footer}>
                    <FilledButtonM
                        label="Continuer"
                        onPress={() =>
                            navigation.navigate(
                                "summer/app-download",
                                nextParams
                            )
                        }
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            </View>
        </View>
    )
}

const SummaryRow = ({ label, value, note, isLast }) => (
    <View style={[styles.summaryRow, isLast && { borderBottomWidth: 0 }]}>
        <Text style={styles.summaryLabel}>{label}</Text>
        <View style={{ alignItems: "flex-end", flexShrink: 1 }}>
            <Text style={styles.summaryValue}>{value}</Text>
            {note ? <Text style={styles.summaryNote}>{note}</Text> : null}
        </View>
    </View>
)

const InfoRow = ({ icon, text }) => (
    <View style={styles.infoRow}>
        <FontAwesome5 name={icon} size={13} color={theme.colors.primary} style={{ width: 22 }} />
        <Text style={styles.infoText}>{text}</Text>
    </View>
)

const styles = StyleSheet.create({
    root: { flex: 1, alignItems: "center", backgroundColor: theme.colors.whiteAlmost },
    frame: { flex: 1, backgroundColor: theme.colors.white, paddingHorizontal: 20 },
    content: { paddingTop: 20, paddingBottom: 24, alignItems: "center" },
    successIcon: {
        width: 64, height: 64, borderRadius: 32,
        backgroundColor: theme.colors.successLight,
        alignItems: "center", justifyContent: "center", marginBottom: 14,
    },
    title: { fontFamily: "Montserrat-ExtraBold", fontSize: 22, color: theme.colors.primary, marginBottom: 6, textAlign: "center" },
    subtitle: { fontFamily: "Montserrat-Regular", fontSize: 14, color: theme.colors.grey, textAlign: "center", marginBottom: 8 },
    emailHint: { fontFamily: "Montserrat-Regular", fontSize: 12, color: theme.colors.grey, textAlign: "center", marginBottom: 18 },
    summary: {
        width: "100%",
        borderWidth: 1, borderColor: theme.colors.lightMore, borderRadius: 14,
        paddingHorizontal: 16, marginBottom: 16,
    },
    summaryRow: {
        flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start",
        paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: theme.colors.lightMore, gap: 12,
    },
    summaryLabel: { fontFamily: "Montserrat-Regular", fontSize: 13, color: theme.colors.grey, flex: 1 },
    summaryValue: { fontFamily: "Montserrat-Bold", fontSize: 13, color: theme.colors.dark || theme.colors.primary, textAlign: "right" },
    summaryNote: { fontFamily: "Montserrat-Regular", fontSize: 11, color: theme.colors.grey, marginTop: 2, textAlign: "right" },
    block: { width: "100%", marginTop: 4 },
    blockKicker: { fontFamily: "Montserrat-Bold", fontSize: 12, color: theme.colors.primary, marginBottom: 8, textTransform: "uppercase" },
    infoRow: { flexDirection: "row", alignItems: "center", marginBottom: 8 },
    infoText: { fontFamily: "Montserrat-Regular", fontSize: 13, color: theme.colors.regular || theme.colors.dark, flex: 1 },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
})

export default SummerConfirmationScreen
